import { Panel, PageHeader } from "@/components/admin-shell";
import { useState, useEffect } from "react";
import {
  Shield,
  Trash2,
  Plus,
  Save,
  X,
  Loader2,
  Link2,
  Edit,
  RefreshCw,
  Eye,
  EyeOff,
} from "lucide-react";
import { apiRequest } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const TABS = [
  "Site",
  "Analytics & Scripts",
  "Redirects",
  "Roles & Staff",
  "Maintenance",
  "Backups",
] as const;

interface ISettings {
  title: string;
  taglineBn?: string;
  logoUrl?: string;
  faviconUrl?: string;
  announcement?: string;
  whatsapp?: string;
  email?: string;
  address?: string;
  facebook?: string;
  instagram?: string;
  maintenanceMode: boolean;
  pixels?: {
    ga4?: string;
    fbPixel?: string;
    fbCapiToken?: string;
    gtm?: string;
    tiktok?: string;
    clarity?: string;
  };
  scripts?: {
    header?: string;
    body?: string;
    footer?: string;
  };
  taggingServerUrl?: string;
  mailchimp?: { apiKey?: string; listId?: string };
  webhookUrl?: string;
  debugMode?: boolean;
  domainVerificationToken?: string;
  robotsTxt?: string;
}

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<(typeof TABS)[number]>("Site");

  // Fetch Settings
  const {
    data: settings,
    isLoading,
    error,
  } = useQuery<ISettings>({
    queryKey: ["settings"],
    queryFn: () => apiRequest<ISettings>("/settings"),
  });

  // Update Settings Mutation
  const updateMutation = useMutation({
    mutationFn: (payload: Partial<ISettings>) =>
      apiRequest<ISettings>("/settings", "PATCH", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Settings updated successfully");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to update settings");
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-gold">Loading settings...</div>
    );
  }

  if (error) {
    return (
      <div className="text-red-400 p-6">Error loading settings: {(error as Error).message}</div>
    );
  }

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Brand identity, governance, security and operational controls."
      />

      <div className="mb-6 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-full border px-4 py-1.5 text-xs uppercase tracking-widest transition cursor-pointer ${
              tab === t
                ? "border-gold bg-gold text-primary-foreground font-semibold"
                : "border-gold/25 text-foreground/80 hover:border-gold/60"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Site" && settings && (
        <SiteSettings
          settings={settings}
          onSave={(data) => updateMutation.mutate(data)}
          isSaving={updateMutation.isPending}
        />
      )}
      {tab === "Analytics & Scripts" && settings && (
        <AnalyticsSettings
          settings={settings}
          onSave={(data) => updateMutation.mutate(data)}
          isSaving={updateMutation.isPending}
        />
      )}
      {tab === "Redirects" && <RedirectsSettings />}
      {tab === "Roles & Staff" && <RolesSettings />}
      {tab === "Maintenance" && settings && (
        <MaintenanceSettings
          settings={settings}
          onToggle={(on) => updateMutation.mutate({ maintenanceMode: on })}
        />
      )}
      {tab === "Backups" && <BackupsSettings />}
    </>
  );
}

function SiteSettings({
  settings,
  onSave,
  isSaving,
}: {
  settings: ISettings;
  onSave: (data: Partial<ISettings>) => void;
  isSaving: boolean;
}) {
  const [title, setTitle] = useState(settings.title || "");
  const [taglineBn, setTaglineBn] = useState(settings.taglineBn || "");
  const [logoUrl, setLogoUrl] = useState(settings.logoUrl || "");
  const [faviconUrl, setFaviconUrl] = useState(settings.faviconUrl || "");
  const [announcement, setAnnouncement] = useState(settings.announcement || "");

  const [whatsapp, setWhatsapp] = useState(settings.whatsapp || "");
  const [email, setEmail] = useState(settings.email || "");
  const [address, setAddress] = useState(settings.address || "");
  const [facebook, setFacebook] = useState(settings.facebook || "");
  const [instagram, setInstagram] = useState(settings.instagram || "");

  useEffect(() => {
    setTitle(settings.title || "");
    setTaglineBn(settings.taglineBn || "");
    setLogoUrl(settings.logoUrl || "");
    setFaviconUrl(settings.faviconUrl || "");
    setAnnouncement(settings.announcement || "");
    setWhatsapp(settings.whatsapp || "");
    setEmail(settings.email || "");
    setAddress(settings.address || "");
    setFacebook(settings.facebook || "");
    setInstagram(settings.instagram || "");
  }, [settings]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      title,
      taglineBn,
      logoUrl,
      faviconUrl,
      announcement,
      whatsapp,
      email,
      address,
      facebook,
      instagram,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Brand identity">
          <div className="space-y-4 text-sm">
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">Site Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1 w-full rounded-md border border-gold/25 bg-background/40 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">
                Tagline (Bengali)
              </label>
              <input
                value={taglineBn}
                onChange={(e) => setTaglineBn(e.target.value)}
                className="mt-1 w-full rounded-md border border-gold/25 bg-background/40 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">Logo URL</label>
              <input
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                className="mt-1 w-full rounded-md border border-gold/25 bg-background/40 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">
                Favicon URL
              </label>
              <input
                value={faviconUrl}
                onChange={(e) => setFaviconUrl(e.target.value)}
                className="mt-1 w-full rounded-md border border-gold/25 bg-background/40 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">
                Announcement bar
              </label>
              <input
                value={announcement}
                onChange={(e) => setAnnouncement(e.target.value)}
                className="mt-1 w-full rounded-md border border-gold/25 bg-background/40 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
              />
            </div>
          </div>
        </Panel>

        <Panel title="Contact & social">
          <div className="space-y-4 text-sm">
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">
                WhatsApp number
              </label>
              <input
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                className="mt-1 w-full rounded-md border border-gold/25 bg-background/40 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">
                Support email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-md border border-gold/25 bg-background/40 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">
                Office address
              </label>
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="mt-1 w-full rounded-md border border-gold/25 bg-background/40 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">
                Facebook URL
              </label>
              <input
                value={facebook}
                onChange={(e) => setFacebook(e.target.value)}
                className="mt-1 w-full rounded-md border border-gold/25 bg-background/40 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gold-soft">
                Instagram URL
              </label>
              <input
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                className="mt-1 w-full rounded-md border border-gold/25 bg-background/40 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
              />
            </div>
          </div>
        </Panel>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-md bg-gold px-6 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50 transition cursor-pointer"
        >
          <Save className="h-4 w-4" />
          {isSaving ? "Saving Settings..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────
// Analytics & Scripts Settings (Module 05)
// ─────────────────────────────────────────────────────────────
function AnalyticsSettings({
  settings,
  onSave,
  isSaving,
}: {
  settings: ISettings;
  onSave: (data: Partial<ISettings>) => void;
  isSaving: boolean;
}) {
  const [ga4, setGa4] = useState(settings.pixels?.ga4 || "");
  const [fbPixel, setFbPixel] = useState(settings.pixels?.fbPixel || "");
  const [fbCapiToken, setFbCapiToken] = useState(settings.pixels?.fbCapiToken || "");
  const [gtm, setGtm] = useState(settings.pixels?.gtm || "");
  const [tiktok, setTiktok] = useState(settings.pixels?.tiktok || "");
  const [clarity, setClarity] = useState(settings.pixels?.clarity || "");
  const [headerScript, setHeaderScript] = useState(settings.scripts?.header || "");
  const [bodyScript, setBodyScript] = useState(settings.scripts?.body || "");
  const [footerScript, setFooterScript] = useState(settings.scripts?.footer || "");
  const [taggingServerUrl, setTaggingServerUrl] = useState(settings.taggingServerUrl || "");
  const [mailchimpApiKey, setMailchimpApiKey] = useState(settings.mailchimp?.apiKey || "");
  const [mailchimpListId, setMailchimpListId] = useState(settings.mailchimp?.listId || "");
  const [webhookUrl, setWebhookUrl] = useState(settings.webhookUrl || "");
  const [debugMode, setDebugMode] = useState(settings.debugMode ?? false);
  const [domainToken, setDomainToken] = useState(settings.domainVerificationToken || "");
  const [robotsTxt, setRobotsTxt] = useState(settings.robotsTxt || "");

  useEffect(() => {
    setGa4(settings.pixels?.ga4 || "");
    setFbPixel(settings.pixels?.fbPixel || "");
    setFbCapiToken(settings.pixels?.fbCapiToken || "");
    setGtm(settings.pixels?.gtm || "");
    setTiktok(settings.pixels?.tiktok || "");
    setClarity(settings.pixels?.clarity || "");
    setHeaderScript(settings.scripts?.header || "");
    setBodyScript(settings.scripts?.body || "");
    setFooterScript(settings.scripts?.footer || "");
    setTaggingServerUrl(settings.taggingServerUrl || "");
    setMailchimpApiKey(settings.mailchimp?.apiKey || "");
    setMailchimpListId(settings.mailchimp?.listId || "");
    setWebhookUrl(settings.webhookUrl || "");
    setDebugMode(settings.debugMode ?? false);
    setDomainToken(settings.domainVerificationToken || "");
    setRobotsTxt(settings.robotsTxt || "");
  }, [settings]);

  const inputCls =
    "mt-1 w-full rounded-md border border-gold/25 bg-background/40 px-3 py-2 text-sm text-foreground outline-none focus:border-gold font-mono placeholder:font-sans";
  const labelCls = "text-xs uppercase tracking-widest text-gold-soft";
  const textareaCls =
    "mt-1 w-full rounded-md border border-gold/25 bg-background/40 px-3 py-2 text-xs text-foreground outline-none focus:border-gold font-mono resize-y";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      pixels: { ga4, fbPixel, fbCapiToken, gtm, tiktok, clarity },
      scripts: { header: headerScript, body: bodyScript, footer: footerScript },
      taggingServerUrl,
      mailchimp: { apiKey: mailchimpApiKey, listId: mailchimpListId },
      webhookUrl,
      debugMode,
      domainVerificationToken: domainToken,
      robotsTxt,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="5.3 — Native Pixel Integrations">
          <div className="space-y-4 text-sm">
            <div>
              <label className={labelCls}>GA4 Measurement ID</label>
              <input
                value={ga4}
                onChange={(e) => setGa4(e.target.value)}
                placeholder="G-XXXXXXXXXX"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Meta / Facebook Pixel ID</label>
              <input
                value={fbPixel}
                onChange={(e) => setFbPixel(e.target.value)}
                placeholder="123456789012345"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Meta CAPI Access Token</label>
              <input
                type="password"
                value={fbCapiToken}
                onChange={(e) => setFbCapiToken(e.target.value)}
                placeholder="EAAxxxxxxxxxxxxxxx..."
                className={inputCls}
              />
              <p className="mt-1 text-[10px] text-muted-foreground">
                Server-side purchase event deduplication via Meta CAPI.
              </p>
            </div>
            <div>
              <label className={labelCls}>GTM Container ID</label>
              <input
                value={gtm}
                onChange={(e) => setGtm(e.target.value)}
                placeholder="GTM-XXXXXX"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>TikTok Pixel ID</label>
              <input
                value={tiktok}
                onChange={(e) => setTiktok(e.target.value)}
                placeholder="C9XXXXXXXXXXXXX"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Microsoft Clarity Project ID</label>
              <input
                value={clarity}
                onChange={(e) => setClarity(e.target.value)}
                placeholder="xxxxxxxxxx"
                className={inputCls}
              />
            </div>
          </div>
        </Panel>

        <Panel title="5.2 — Server-Side Tracking">
          <div className="space-y-4 text-sm">
            <div>
              <label className={labelCls}>Tagging Server URL (Stape / GCP)</label>
              <input
                value={taggingServerUrl}
                onChange={(e) => setTaggingServerUrl(e.target.value)}
                placeholder="https://gtm.yourdomain.com"
                className={inputCls}
              />
              <p className="mt-1 text-[10px] text-muted-foreground">
                Used as the GTM transport_url. Leave blank for Google's default endpoint.
              </p>
            </div>
            <div className="rounded-md border border-gold/15 bg-accent/20 p-3 text-xs text-muted-foreground">
              <p className="font-semibold text-foreground mb-1">Event ID Deduplication ✓</p>
              <p>
                Every order generates a unique eventId shared between the browser pixel and Meta
                CAPI — preventing double-counting in Ads Manager.
              </p>
            </div>
          </div>
        </Panel>

        <Panel title="5.4 — Mailchimp Newsletter Sync">
          <div className="space-y-4 text-sm">
            <div>
              <label className={labelCls}>Mailchimp API Key</label>
              <input
                type="password"
                value={mailchimpApiKey}
                onChange={(e) => setMailchimpApiKey(e.target.value)}
                placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx-us21"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Audience / List ID</label>
              <input
                value={mailchimpListId}
                onChange={(e) => setMailchimpListId(e.target.value)}
                placeholder="a1b2c3d4e5"
                className={inputCls}
              />
            </div>
            <div className="rounded-md border border-gold/15 bg-accent/20 p-3 text-xs text-muted-foreground">
              Subscribers are synced to Mailchimp instantly when they join via the storefront footer
              or popup. Duplicates handled automatically.
            </div>
          </div>
        </Panel>

        <Panel title="5.5 — Webhooks & API Debugger">
          <div className="space-y-4 text-sm">
            <div>
              <label className={labelCls}>Webhook URL (Zapier / Make.com)</label>
              <input
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://hooks.zapier.com/hooks/catch/..."
                className={inputCls}
              />
              <p className="mt-1 text-[10px] text-muted-foreground">
                Receives an order.created POST payload on every confirmed order.
              </p>
            </div>
            <div className="flex items-center justify-between rounded-md border border-gold/15 bg-accent/20 p-3">
              <div>
                <div className="text-foreground text-sm font-medium">dataLayer Debug Mode</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Log analytics events to the browser console.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDebugMode(!debugMode)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition cursor-pointer ${debugMode ? "bg-gold" : "bg-accent border border-gold/20"}`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-background transition ${debugMode ? "translate-x-4" : "translate-x-0.5"}`}
                />
              </button>
            </div>
          </div>
        </Panel>

        <Panel title="5.6 — Domain Verification Token">
          <div className="space-y-3 text-sm">
            <div>
              <label className={labelCls}>Verification HTML / Meta Tag</label>
              <textarea
                rows={3}
                value={domainToken}
                onChange={(e) => setDomainToken(e.target.value)}
                placeholder={'<meta name="google-site-verification" content="..." />'}
                className={textareaCls}
              />
            </div>
            <div className="rounded-md border border-gold/15 bg-accent/20 p-3 text-xs text-muted-foreground space-y-1">
              <p>After saving, point your verifier to:</p>
              <code className="text-gold break-all">/api/v1/settings/verify/domain</code>
              <p className="mt-1">
                Works for Google Search Console, Meta Business Manager, and similar platforms.
              </p>
            </div>
          </div>
        </Panel>
      </div>

      <Panel title="5.1 — Script Injection Manager">
        <div className="grid lg:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Header Scripts (inside &lt;head&gt;)</label>
            <textarea
              rows={8}
              value={headerScript}
              onChange={(e) => setHeaderScript(e.target.value)}
              placeholder={
                "<!-- Google Search Console verification -->\n<!-- Custom CSS / meta tags -->"
              }
              className={textareaCls}
            />
          </div>
          <div>
            <label className={labelCls}>Body Scripts (after &lt;body&gt; open)</label>
            <textarea
              rows={8}
              value={bodyScript}
              onChange={(e) => setBodyScript(e.target.value)}
              placeholder={"<!-- GTM noscript fallback -->"}
              className={textareaCls}
            />
          </div>
          <div>
            <label className={labelCls}>Footer Scripts (before &lt;/body&gt;)</label>
            <textarea
              rows={8}
              value={footerScript}
              onChange={(e) => setFooterScript(e.target.value)}
              placeholder={"<!-- Tawk.to / WhatsApp chat widget -->"}
              className={textareaCls}
            />
          </div>
        </div>
      </Panel>

      <Panel title="7.3 — Search Engine Indexing (robots.txt)">
        <div className="space-y-4 text-sm">
          <div>
            <label className={labelCls}>Robots.txt Content</label>
            <textarea
              rows={6}
              value={robotsTxt}
              onChange={(e) => setRobotsTxt(e.target.value)}
              placeholder={`User-agent: *\nAllow: /\nDisallow: /account\nDisallow: /checkout\nDisallow: /cart\n\nSitemap: ${window.location.origin}/sitemap.xml`}
              className={textareaCls}
            />
            <p className="mt-1 text-[10px] text-muted-foreground">
              Define crawl directives for crawler bots. Leave empty to use the system default rules.
            </p>
          </div>
        </div>
      </Panel>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-md bg-gold px-6 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50 transition cursor-pointer"
        >
          <Save className="h-4 w-4" />
          {isSaving ? "Saving..." : "Save Analytics Settings"}
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────
// 7.6 Broken Link & Redirect Manager (301 Redirect)
// ─────────────────────────────────────────────────────────────
function RedirectsSettings() {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [fromPath, setFromPath] = useState("");
  const [toPath, setToPath] = useState("");
  const [statusCode, setStatusCode] = useState<301 | 302>(301);
  const [note, setNote] = useState("");

  const {
    data: redirects = [],
    isLoading,
    error,
  } = useQuery<any[]>({
    queryKey: ["redirects"],
    queryFn: () => apiRequest<any[]>("/redirects"),
  });

  const createMutation = useMutation({
    mutationFn: (payload: { from: string; to: string; statusCode: 301 | 302; note?: string }) =>
      apiRequest<any>("/redirects", "POST", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["redirects"] });
      toast.success("Redirect rule created successfully!");
      setIsOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to create redirect");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      apiRequest<any>(`/redirects/${id}`, "PATCH", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["redirects"] });
      toast.success("Redirect rule updated successfully!");
      setIsOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to update redirect");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/redirects/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["redirects"] });
      toast.success("Redirect rule removed");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to remove redirect");
    },
  });

  const resetForm = () => {
    setFromPath("");
    setToPath("");
    setStatusCode(301);
    setNote("");
    setEditingId(null);
  };

  const handleEdit = (r: any) => {
    setEditingId(r._id);
    setFromPath(r.from);
    setToPath(r.to);
    setStatusCode(r.statusCode);
    setNote(r.note || "");
    setIsOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fromPath.trim() || !toPath.trim()) {
      toast.error("Source and Target paths are required");
      return;
    }

    const payload = {
      from: fromPath.trim(),
      to: toPath.trim(),
      statusCode,
      note: note.trim() || undefined,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const inputCls =
    "w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold";
  const labelCls = "block text-xs uppercase tracking-wider text-gold-soft mb-1";

  return (
    <>
      <Panel
        title="URL Redirects (301/302 Redirect Manager)"
        description="Preserve your SEO rank juices and handle broken links by setting up redirection mappings."
      >
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => {
              resetForm();
              setIsOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-md border border-gold/30 px-3 py-1.5 text-xs text-gold hover:bg-accent cursor-pointer transition"
          >
            <Plus className="h-3.5 w-3.5" /> Add Redirect Route
          </button>
        </div>

        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-gold">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gold border-t-transparent mr-2" />
            Loading redirects...
          </div>
        ) : error ? (
          <div className="text-red-400 text-sm">
            Error loading redirects: {(error as Error).message}
          </div>
        ) : redirects.length === 0 ? (
          <div className="flex h-32 flex-col items-center justify-center text-center text-muted-foreground">
            <Link2 className="h-8 w-8 text-gold/30 mb-2" />
            <p className="text-sm">No redirects active.</p>
            <p className="text-xs text-muted-foreground/75 mt-1">
              Redirections will help prevent 404 search indexing penalties.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-gold/15 text-gold-soft text-xs uppercase tracking-wider">
                  <th className="py-2.5 px-3 font-semibold">Source (From)</th>
                  <th className="py-2.5 px-3 font-semibold">Destination (To)</th>
                  <th className="py-2.5 px-3 font-semibold">Status</th>
                  <th className="py-2.5 px-3 font-semibold text-center">Hits</th>
                  <th className="py-2.5 px-3 font-semibold">Note</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gold/10">
                {redirects.map((r) => (
                  <tr key={r._id} className="hover:bg-accent/20 transition">
                    <td className="py-3 px-3 font-mono text-xs text-foreground/95 break-all max-w-[200px]">
                      {r.from}
                    </td>
                    <td className="py-3 px-3 font-mono text-xs text-gold/90 break-all max-w-[200px]">
                      {r.to}
                    </td>
                    <td className="py-3 px-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${
                          r.statusCode === 301
                            ? "bg-emerald-500/10 text-emerald-400"
                            : "bg-blue-500/10 text-blue-400"
                        }`}
                      >
                        {r.statusCode}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center font-mono text-xs font-semibold text-foreground/80">
                      {r.hitCount || 0}
                    </td>
                    <td
                      className="py-3 px-3 text-xs text-muted-foreground max-w-[150px] truncate"
                      title={r.note}
                    >
                      {r.note || "—"}
                    </td>
                    <td className="py-3 px-3 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          onClick={() => handleEdit(r)}
                          className="p-1 text-muted-foreground hover:text-gold transition cursor-pointer"
                          title="Edit redirect rule"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Delete redirect rule from "${r.from}"?`)) {
                              deleteMutation.mutate(r._id);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          className="p-1 text-muted-foreground hover:text-red-400 transition cursor-pointer disabled:opacity-50"
                          title="Delete redirect rule"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Modal Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-xl border border-gold/30 bg-background p-6 shadow-2xl animate-fade-in">
            <div className="flex items-center justify-between border-b border-gold/15 pb-3">
              <h3 className="font-serif text-lg text-gold flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                {editingId ? "Edit Redirect Rule" : "Create Redirect Rule"}
              </h3>
              <button
                onClick={() => {
                  setIsOpen(false);
                  resetForm();
                }}
                className="text-muted-foreground hover:text-foreground transition cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div>
                <label className={labelCls}>Source Path (From) *</label>
                <input
                  type="text"
                  required
                  value={fromPath}
                  onChange={(e) => setFromPath(e.target.value)}
                  placeholder="/old-perfume-page"
                  className={inputCls}
                />
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Must start with a slash (e.g. <code>/shop-old</code>)
                </p>
              </div>

              <div>
                <label className={labelCls}>Destination URL or Path *</label>
                <input
                  type="text"
                  required
                  value={toPath}
                  onChange={(e) => setToPath(e.target.value)}
                  placeholder="/products/new-oud-royale"
                  className={inputCls}
                />
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Relative path or absolute URL (e.g. <code>/products/oud-royale-50ml</code>)
                </p>
              </div>

              <div>
                <label className={labelCls}>HTTP Status Code</label>
                <select
                  value={statusCode}
                  onChange={(e) => setStatusCode(Number(e.target.value) as 301 | 302)}
                  className={`${inputCls} bg-background`}
                >
                  <option value={301}>301 (Permanent Redirect - Recommended for SEO)</option>
                  <option value={302}>302 (Temporary Redirect)</option>
                </select>
              </div>

              <div>
                <label className={labelCls}>Administrative Note (Optional)</label>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Migrated URL after product rename"
                  className={inputCls}
                />
              </div>

              <div className="pt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    resetForm();
                  }}
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="rounded border border-gold/30 px-4 py-2 text-xs font-semibold text-gold hover:bg-accent/40 cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded bg-gold px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50 cursor-pointer"
                >
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}
                  {editingId ? "Save Changes" : "Create Redirect"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function RolesSettings() {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [staffName, setStaffName] = useState("");
  const [staffEmail, setStaffEmail] = useState("");
  const [staffPhone, setStaffPhone] = useState("");
  const [staffPassword, setStaffPassword] = useState("");
  const [showStaffPassword, setShowStaffPassword] = useState(false);

  const {
    data: staff = [],
    isLoading,
    error,
  } = useQuery<any[]>({
    queryKey: ["staff"],
    queryFn: () => apiRequest<any[]>("/users?role=admin"),
  });

  const createMutation = useMutation({
    mutationFn: (payload: { name: string; email?: string; phone: string; password: string }) =>
      apiRequest<any>("/users/staff", "POST", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      toast.success("Staff admin created successfully!");
      setIsOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to create staff admin");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/users/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      toast.success("Staff member removed");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to remove staff member");
    },
  });

  const resetForm = () => {
    setStaffName("");
    setStaffEmail("");
    setStaffPhone("");
    setStaffPassword("");
    setShowStaffPassword(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!staffName.trim() || !staffPhone.trim() || !staffPassword.trim()) {
      toast.error("Name, phone and password are required");
      return;
    }
    createMutation.mutate({
      name: staffName.trim(),
      email: staffEmail.trim() || undefined,
      phone: staffPhone.trim(),
      password: staffPassword,
    });
  };

  return (
    <>
      <Panel
        title="Staff & permissions"
        description="Registered administrators with dashboard access privileges."
      >
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => setIsOpen(true)}
            className="inline-flex items-center gap-2 rounded-md border border-gold/30 px-3 py-1.5 text-xs text-gold hover:bg-accent cursor-pointer transition"
          >
            <Plus className="h-3.5 w-3.5" /> Add staff admin
          </button>
        </div>

        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-gold">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gold border-t-transparent mr-2" />
            Loading staff...
          </div>
        ) : error ? (
          <div className="text-red-400 text-sm">
            Error loading staff: {(error as Error).message}
          </div>
        ) : staff.length === 0 ? (
          <div className="flex h-32 flex-col items-center justify-center text-center text-muted-foreground">
            <Shield className="h-8 w-8 text-gold/30 mb-2" />
            <p className="text-sm">No staff members found.</p>
            <p className="text-xs mt-1">Add an admin to grant dashboard access.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-gold-soft border-b border-gold/10">
                  <th className="py-2 pb-3">Member</th>
                  <th className="pb-3">Email</th>
                  <th className="pb-3">Phone</th>
                  <th className="pb-3">Role</th>
                  <th className="pb-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gold/10">
                {staff.map((s) => (
                  <tr key={s._id || s.email} className="hover:bg-accent/15 transition-colors">
                    <td className="py-3 flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gold/10 border border-gold/30 font-display text-gold text-sm">
                        {s.name ? s.name[0].toUpperCase() : "A"}
                      </div>
                      <span className="font-medium text-foreground">
                        {s.name || "Administrator"}
                      </span>
                    </td>
                    <td className="text-muted-foreground">{s.email || "—"}</td>
                    <td className="text-muted-foreground font-mono text-xs">{s.phone || "—"}</td>
                    <td>
                      <span className="rounded-full border border-gold/40 bg-gold/10 px-2.5 py-0.5 text-[11px] text-gold capitalize font-semibold">
                        {s.role || "admin"}
                      </span>
                    </td>
                    <td className="text-right">
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              `Remove "${s.name || "this staff member"}" from admin access?`,
                            )
                          ) {
                            deleteMutation.mutate(s._id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        className="rounded border border-gold/15 p-1 text-muted-foreground hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/30 transition cursor-pointer disabled:opacity-40"
                        title="Remove staff member"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Add Staff Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-md rounded-xl border border-gold/30 bg-brand-card p-6 shadow-2xl">
            <button
              onClick={() => {
                setIsOpen(false);
                resetForm();
              }}
              className="absolute top-4 right-4 rounded p-1 text-muted-foreground hover:text-gold"
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="font-display text-xl text-gold mb-1">Create Staff Admin</h2>
            <p className="text-xs text-muted-foreground mb-5">
              Grant a user full dashboard access. They will receive the{" "}
              <span className="text-gold">admin</span> role.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={staffName}
                  onChange={(e) => setStaffName(e.target.value)}
                  placeholder="e.g. Rahul Karim"
                  className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                  Phone (BD) *
                </label>
                <input
                  type="tel"
                  required
                  value={staffPhone}
                  onChange={(e) => setStaffPhone(e.target.value)}
                  placeholder="01XXXXXXXXX"
                  className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold font-mono"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                  Email (optional)
                </label>
                <input
                  type="email"
                  value={staffEmail}
                  onChange={(e) => setStaffEmail(e.target.value)}
                  placeholder="staff@example.com"
                  className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                  Temporary Password *
                </label>
                <div className="relative flex items-center">
                  <input
                    type={showStaffPassword ? "text" : "password"}
                    required
                    value={staffPassword}
                    onChange={(e) => setStaffPassword(e.target.value)}
                    placeholder="Min. 6 characters"
                    className="w-full rounded border border-gold/25 bg-accent/25 pl-3 pr-10 py-2 text-sm text-foreground outline-none focus:border-gold"
                  />
                  <button
                    type="button"
                    onClick={() => setShowStaffPassword(!showStaffPassword)}
                    className="absolute right-3 text-gold/60 hover:text-gold transition-colors focus:outline-none"
                    tabIndex={-1}
                  >
                    {showStaffPassword ? (
                      <EyeOff className="h-4.5 w-4.5" />
                    ) : (
                      <Eye className="h-4.5 w-4.5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="pt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    resetForm();
                  }}
                  disabled={createMutation.isPending}
                  className="rounded border border-gold/30 px-4 py-2 text-xs font-semibold text-gold hover:bg-accent/40 cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded bg-gold px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50 cursor-pointer"
                >
                  {createMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Create Admin
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function MaintenanceSettings({
  settings,
  onToggle,
}: {
  settings: ISettings;
  onToggle: (on: boolean) => void;
}) {
  const queryClient = useQueryClient();

  const clearCacheMutation = useMutation({
    mutationFn: () => apiRequest<any>("/settings/clear-cache", "POST"),
    onSuccess: () => {
      // Clear all react-query caches in browser
      queryClient.clear();
      toast.success("Caches successfully cleared on the server & client!");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to clear server caches");
    },
  });

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Panel title="Maintenance mode">
        <div className="rounded-md border border-gold/20 bg-accent/30 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-foreground">
                {settings.maintenanceMode ? "Site is Offline" : "Site is Live"}
              </div>
              <div className="text-xs text-muted-foreground">
                Toggle to show a "Coming back soon" page to public visitors.
              </div>
            </div>
            <Toggle on={settings.maintenanceMode} onChange={onToggle} />
          </div>
        </div>
        <button
          onClick={() => clearCacheMutation.mutate()}
          disabled={clearCacheMutation.isPending}
          className="mt-4 w-full flex items-center justify-center gap-2 rounded-md border border-gold/30 py-2 text-sm text-gold hover:bg-accent cursor-pointer disabled:opacity-50"
        >
          {clearCacheMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Clearing cache...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4" />
              Clear site cache
            </>
          )}
        </button>
      </Panel>

      <Panel title="Security">
        <ul className="space-y-3 text-sm">
          <li className="flex items-start gap-3 rounded-md border border-gold/15 bg-accent/30 p-3">
            <Shield className="h-4 w-4 mt-0.5 text-gold" />
            <div>
              <div className="text-foreground">Brute force protection active</div>
              <div className="text-xs text-muted-foreground">
                5 failed attempts → temporary lockout configured.
              </div>
            </div>
          </li>
          <li className="flex items-start gap-3 rounded-md border border-gold/15 bg-accent/30 p-3">
            <Shield className="h-4 w-4 mt-0.5 text-gold" />
            <div>
              <div className="text-foreground">Input sanitization active</div>
              <div className="text-xs text-muted-foreground">
                JSON payload limits & validation schemas enforced on all fields.
              </div>
            </div>
          </li>
        </ul>
      </Panel>
    </div>
  );
}

function BackupsSettings() {
  return (
    <Panel title="Database backups">
      <div className="space-y-3">
        {[
          { date: "2026-07-02 03:00", size: "202 MB" },
          { date: "2026-07-01 03:00", size: "201 MB" },
          { date: "2026-06-30 03:00", size: "199 MB" },
        ].map((b) => (
          <div
            key={b.date}
            className="flex items-center justify-between rounded-md border border-gold/15 bg-accent/30 p-3 text-sm"
          >
            <div>
              <div className="font-mono">{b.date}</div>
              <div className="text-xs text-muted-foreground">Full MongoDB backup · {b.size}</div>
            </div>
            <button
              onClick={() => toast.success("Downloading backup file...")}
              className="rounded-md border border-gold/30 px-3 py-1 text-xs text-gold hover:bg-accent cursor-pointer"
            >
              Download
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={() => toast.success("Creating manual database backup task...")}
        className="mt-5 w-full rounded-md bg-gold px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 cursor-pointer"
      >
        Create backup now
      </button>
    </Panel>
  );
}

function Toggle({ on, onChange }: { on: boolean; onChange: (val: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition cursor-pointer ${
        on ? "bg-gold" : "bg-accent border border-gold/20"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-background transition ${on ? "translate-x-4" : "translate-x-0.5"}`}
      />
    </button>
  );
}
